import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, UserRound, Wallet } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { inr, fmtDate, StatusBadge, badgeClass } from "@/lib/utils";
import AddCustomerDialog from "@/components/AddCustomerDialog";
import AdminAssistedOrderDialog from "@/components/AdminAssistedOrderDialog";

export default function AdminCustomers() {
  const { t, tAny } = useLang();
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [assistedOrderOpen, setAssistedOrderOpen] = useState(false);

  const loadPrivateImage = async (path) => {
    if (!path) {
      setImgSrc(null);
      return;
    }
    try {
      const response = await api.get(
        path.startsWith("/api/") ? path.slice(4) : path,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      setImgSrc((old) => {
        if (old) URL.revokeObjectURL(old);
        return url;
      });
    } catch (e) {
      setImgSrc(null);
    }
  };

  const load = () =>
    api
      .get("/admin/customers", { params: { q } })
      .then((r) => setCustomers(r.data))
      .catch((e) => toast.error(fmtErr(e)));

  useEffect(() => {
    load();
  }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/customers/${id}`);
      setSelected(data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  useEffect(() => {
    loadPrivateImage(selected?.photo_url);
    return () => {
      setImgSrc((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
    };
  }, [selected?.photo_url]);

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">
            {t("customersTitle")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t("customersSub")}</p>
        </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100">
              {customers.length} customers shown
            </span>
          </div>

        <button
          type="button"
          className="sf-btn sf-btn-primary inline-flex items-center gap-2 px-5 py-2.5 shadow-sm"
          onClick={() => setAddCustomerOpen(true)}
        >
          <UserRound className="h-4 w-4" />
          Add Customer
        </button>
      </div>

      <div className="sf-card p-4">
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Search Customers
          </label>
          <div className="relative max-w-2xl">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="customer-search-input"
              className="sf-input pl-9 w-full h-11"
              placeholder="Search by name, mobile, licence or POS ID..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
          </div>
        </div>
      </div>

      <div className="sf-card overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr>
              <th className="sf-th">{t("customerLbl")}</th>
              <th className="sf-th">{t("mobileLbl")}</th>
              <th className="sf-th">{t("licenceNumber")}</th>
              <th className="sf-th">{t("posId")}</th>
              <th className="sf-th">{t("ordersCountCol")}</th>
              <th className="sf-th">{t("totalDue")}</th>
              <th className="sf-th">{t("registeredCol")}</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr
                key={c.id}
                className="sf-tr cursor-pointer hover:bg-emerald-50/50 transition-colors"
                data-testid={`customer-row-${c.mobile}`}
                onClick={() => openDetail(c.id)}
              >
                <td className="sf-td font-semibold text-slate-900">{c.name}</td>
                <td className="sf-td">{c.mobile}</td>
                <td className="sf-td">{c.license_number}</td>
                <td className="sf-td">{c.pos_id || "—"}</td>
                <td className="sf-td">{c.orders_count > 0 ? <span className="inline-flex min-w-8 items-center justify-center rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700 border border-emerald-100">{c.orders_count}</span> : <span className="text-slate-400">0</span>}</td>
                <td className="sf-td font-semibold text-rose-700">
                  {c.total_due > 0 ? <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-700 border border-rose-100">{inr(c.total_due)}</span> : <span className="text-slate-400">{inr(c.total_due)}</span>}
                </td>
                <td className="sf-td">{fmtDate(c.created_at)}</td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr>
                <td className="sf-td text-slate-400" colSpan={7}>
                  No customers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white text-slate-900 border-slate-300 shadow-2xl"
          data-testid="customer-detail-modal"
        >
          <DialogHeader>
            <DialogTitle>
              {selected?.name} — {t("businessHistory")}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-5">
              <div className="flex gap-5">
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt="customer"
                    className="h-20 w-20 shrink-0 rounded-lg object-cover border"
                    data-testid="customer-photo"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-slate-100 border flex items-center justify-center">
                    <UserRound className="h-8 w-8 text-slate-400" />
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 flex-1 text-sm">
                  <div>
                    <span className="sf-label">{t("mobileLbl")}</span>
                    <span className="font-semibold text-slate-900">
                      +91 {selected.mobile}
                    </span>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <span className="sf-label">{t("licenceNumber")}</span>
                    <span>{selected.license_number}</span>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <span className="sf-label">{t("posId")}</span>
                    <span>{selected.pos_id || "—"}</span>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <span className="sf-label">{t("addressLabelC")}</span>
                    <span>{selected.address}</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="sf-btn sf-btn-primary w-full sm:w-auto shadow-md hover:shadow-lg transition-shadow"
                onClick={() => setAssistedOrderOpen(true)}
              >
                Create Order for Customer
              </button>

              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                  <div className="sf-label">{t("totalOrders")}</div>
                  <div className="font-bold text-slate-900">
                    {(selected.orders || []).length}
                  </div>
                </div>
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                  <div className="sf-label">{t("totalPaid")}</div>
                  <div className="font-bold text-emerald-700">
                    {inr(
                      (selected.orders || []).reduce(
                        (s, o) => s + (Number(o.payment?.paid) || 0),
                        0,
                      ),
                    )}
                  </div>
                </div>
                <div
                  className="rounded-lg bg-emerald-50 border border-emerald-100 p-3"
                  data-testid="customer-total-due"
                >
                  <div className="sf-label flex items-center gap-1">
                    <Wallet className="h-3 w-3" /> {t("outstandingDue")}
                  </div>
                  <div className="font-bold text-rose-700">
                    {inr(
                      (selected.orders || []).reduce(
                        (s, o) => s + (Number(o.payment?.due) || 0),
                        0,
                      ),
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="sf-th">{t("orderNo")}</th>
                      <th className="sf-th">{t("date")}</th>
                      <th className="sf-th">{t("total")}</th>
                      <th className="sf-th">{t("due")}</th>
                      <th className="sf-th">{t("payment")}</th>
                      <th className="sf-th">{t("status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.orders || []).map((o) => (
                      <tr
                        key={o.id}
                        data-testid={`customer-order-${o.order_number}`}
                      >
                        <td className="sf-td font-mono-sf font-semibold">
                          {o.order_number}
                        </td>
                        <td className="sf-td">{fmtDate(o.created_at)}</td>
                        <td className="sf-td">
                          {o.payment.total > 0 ? inr(o.payment.total) : "—"}
                        </td>
                        <td className="sf-td">{inr(o.payment.due)}</td>
                        <td className="sf-td">
                          <span
                            className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${badgeClass(o.payment.status)}`}
                          >
                            {o.payment.status}
                          </span>
                        </td>
                        <td className="sf-td">
                          <StatusBadge status={o.status} />
                        </td>
                      </tr>
                    ))}
                    {(selected.orders || []).length === 0 && (
                      <tr>
                        <td className="sf-td text-slate-400" colSpan={6}>
                          {t("noneYet")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div
                className="rounded-lg border border-slate-200 p-4"
                data-testid="customer-notifications"
              >
                <div className="text-sm font-bold text-slate-900 mb-2">
                  {t("recentNotifs")}
                </div>
                <div className="space-y-1.5 text-xs text-slate-600 max-h-40 overflow-y-auto">
                  {(selected.notifications || []).map((n, i) => (
                    <div
                      key={i}
                      className="border-b border-slate-100 pb-1.5"
                      data-testid={`customer-notification-${i}`}
                    >
                      <span className="text-slate-400">
                        {fmtDate(n.created_at)} · {n.mode}:
                      </span>{" "}
                      {n.message}
                    </div>
                  ))}
                  {(selected.notifications || []).length === 0 && (
                    <div className="text-slate-400">{t("noneYet")}</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AdminAssistedOrderDialog
        open={assistedOrderOpen}
        onClose={() => setAssistedOrderOpen(false)}
        customer={selected}
        onCreated={() => {
          setAssistedOrderOpen(false);
          if (selected?.id) openDetail(selected.id);
          load();
        }}
      />

      <AddCustomerDialog
        open={addCustomerOpen}
        onClose={() => setAddCustomerOpen(false)}
        onCreated={() => {
          setAddCustomerOpen(false);
          load();
        }}
      />
    </div>
  );
}
